import { EFinancingCompany } from '@if/core/types'
import { DateOnly, format } from '@if/core/date'
import { formatCurrency, formatDate } from '@if/core/utils'
import { Caption, CollapsibleListItem, ErrorAlert, FieldLabel, FieldValue, InvoiceTable } from '@if/mui'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useGetJournalTransactions, type JournalTransactionLineSummary } from '@src/api/loan/loan-api'
import {
  ESystemAccount,
  getESystemAccountList,
  OWNER_ACCOUNT_PREFIX_SPAN,
  SYSTEM_ACCOUNT_PRODUCT_ID,
} from '@src/types/constants'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  loanId: string
  financingCompanyId: EFinancingCompany
}

// Both fields are ISO strings, so ordering compares lexicographically.
type SortField = 'effectiveDate' | 'createdOn'

const UNRECONCILED_PERIOD_ID = -1

// Fixed column widths shared by the header band and every row, so the list still reads as a table.
const DATE_WIDTH = 140
const CREATED_BY_WIDTH = 200
const CREATED_ON_WIDTH = 140
const PERIOD_WIDTH = 150
const AMOUNT_WIDTH = 140
// Right inset of the header band: the rows end with the collapse chevron (19px icon) inside the
// ListItemButton's 16px padding, so the amount column lines up between header and rows.
const CHEVRON_INSET = '35px'

// InvoiceTable's 'currency' columns always render a figure, so the debit/credit columns are
// 'label' columns fed these right-aligned cells: the shared currency format, but a dash for zero.
const AmountCell = ({ value }: { value: number }) => (
  <Box component="span" sx={{ display: 'block', textAlign: 'right' }}>
    {value === 0 ? '—' : formatCurrency(value)}
  </Box>
)

const AmountHeader = ({ label }: { label: string }) => (
  <Box component="span" sx={{ display: 'block', textAlign: 'right' }}>
    {label}
  </Box>
)

// PeriodId is yyyyMM (e.g. 201803 = March 2018); null = not reported yet, -1 = not reconciled (legacy data).
const formatPeriod = (
  periodId: number | null,
  locale: string,
  currentLabel: string,
  notReconciledLabel: string,
): string => {
  if (periodId == null) return currentLabel
  if (periodId === UNRECONCILED_PERIOD_ID) return notReconciledLabel
  const year = Math.floor(periodId / 100)
  const month = periodId % 100
  if (month < 1 || month > 12) return String(periodId)
  const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1))
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const LoanTransactionList = ({ loanId, financingCompanyId }: Props) => {
  const { t, i18n } = useTranslation()
  const [transactions, isPending, error] = useGetJournalTransactions(loanId, financingCompanyId)
  const [sortField, setSortField] = useState<SortField>('effectiveDate')

  // The API already orders newest-first, but the display order is this screen's contract —
  // don't depend on the SQL keeping it.
  const sortedTransactions = useMemo(
    () => [...(transactions ?? [])].sort((a, b) => b[sortField].localeCompare(a[sortField]) || b.id - a.id),
    [transactions, sortField],
  )

  // Lines whose account, not product, decides the label.
  const particularLineDescription = (line: JournalTransactionLineSummary): string | undefined => {
    const baseAccount = (line.accountNumber % OWNER_ACCOUNT_PREFIX_SPAN) as ESystemAccount
    // A closing moves every receivableproduct onto the closing auxiliary account while keeping its product id,
    // so that line is named after the account rather than the product it still carries.
    if (baseAccount === ESystemAccount.ClosingAuxiliary) return t(`enum.ESystemAccount.${baseAccount}`)
    return undefined
  }

  // A line names either a product or, when ProductId is -1, the system GL account its
  // number resolves to (net of the owner's whole-million prefix).
  const lineDescription = (line: JournalTransactionLineSummary): string => {
    const particularDescription = particularLineDescription(line)
    if (particularDescription !== undefined) return particularDescription
    if (line.productId !== SYSTEM_ACCOUNT_PRODUCT_ID) {
      const product = t(`enum.product.${line.productId}`)
      // `== null` also covers a stale payload that predates the `accountType` field.
      return line.accountType == null ? product : `${t(`enum.EAccountType.${line.accountType}`)} - ${product}`
    }
    const baseAccount = line.accountNumber % OWNER_ACCOUNT_PREFIX_SPAN
    return (getESystemAccountList() as number[]).includes(baseAccount)
      ? t(`enum.ESystemAccount.${baseAccount as ESystemAccount}`)
      : t('loan.transactions.systemAccount')
  }

  if (isPending)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  if (error || !transactions) return <ErrorAlert error={error ?? undefined} />

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
        <FieldLabel>{t('loan.transactions.sortBy')}</FieldLabel>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortField}
          onChange={(_, value: SortField | null) => value && setSortField(value)}
          aria-label={t('loan.transactions.sortBy')}
        >
          <ToggleButton value="effectiveDate">{t('loan.transactions.date')}</ToggleButton>
          <ToggleButton value="createdOn">{t('loan.transactions.createdOn')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          pr: CHEVRON_INSET,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <FieldLabel sx={{ flex: `0 0 ${DATE_WIDTH}px` }}>{t('loan.transactions.date')}</FieldLabel>
        <FieldLabel sx={{ flex: `0 0 ${CREATED_BY_WIDTH}px` }}>{t('loan.transactions.createdBy')}</FieldLabel>
        <FieldLabel sx={{ flex: `0 0 ${CREATED_ON_WIDTH}px` }}>{t('loan.transactions.createdOn')}</FieldLabel>
        <FieldLabel sx={{ flex: `0 0 ${PERIOD_WIDTH}px` }}>{t('loan.transactions.period')}</FieldLabel>
        <FieldLabel sx={{ flex: 1, minWidth: 0 }}>{t('loan.transactions.description')}</FieldLabel>
        <FieldLabel sx={{ flex: `0 0 ${AMOUNT_WIDTH}px`, textAlign: 'right' }}>
          {t('loan.transactions.amount')}
        </FieldLabel>
      </Box>
      {sortedTransactions.length === 0 ? (
        <Caption sx={{ display: 'block', p: 3, textAlign: 'center' }}>{t('loan.transactions.empty')}</Caption>
      ) : (
        <List disablePadding>
          {sortedTransactions.map((transaction) => (
            <CollapsibleListItem
              key={transaction.id}
              summary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <ListItemText
                    sx={{ flex: `0 0 ${DATE_WIDTH}px`, flexGrow: 0, my: 0 }}
                    primary={format(new DateOnly(transaction.effectiveDate), 'P')}
                  />
                  <FieldValue sx={{ flex: `0 0 ${CREATED_BY_WIDTH}px` }} noWrap>
                    {transaction.createdByName}
                  </FieldValue>
                  <FieldValue sx={{ flex: `0 0 ${CREATED_ON_WIDTH}px` }}>
                    {formatDate(transaction.createdOn)}
                  </FieldValue>
                  <FieldValue sx={{ flex: `0 0 ${PERIOD_WIDTH}px` }}>
                    {formatPeriod(
                      transaction.periodId,
                      i18n.language,
                      t('loan.transactions.periodCurrent'),
                      t('loan.transactions.periodNotReconciled'),
                    )}
                  </FieldValue>
                  <FieldValue sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {transaction.description}
                  </FieldValue>
                  <FieldValue sx={{ flex: `0 0 ${AMOUNT_WIDTH}px`, textAlign: 'right' }}>
                    {formatCurrency(transaction.amount)}
                  </FieldValue>
                </Box>
              }
            >
              {/* `?? []` guards a stale API payload that predates the `lines` field. */}
              <Card>
                <CardContent>
                  <InvoiceTable
                    columns={[
                      { label: t('loan.transactions.account') },
                      { label: t('loan.transactions.description') },
                      { label: <AmountHeader label={t('loan.transactions.debit')} /> },
                      { label: <AmountHeader label={t('loan.transactions.credit')} /> },
                    ]}
                    items={[...(transaction.lines ?? [])]
                      .sort((a, b) => b.accountNumber - a.accountNumber)
                      .map((line) => ({
                        cells: [
                          String(line.accountNumber),
                          lineDescription(line),
                          <AmountCell key="debit" value={line.debit} />,
                          <AmountCell key="credit" value={line.credit} />,
                        ],
                      }))}
                  />
                </CardContent>
              </Card>
            </CollapsibleListItem>
          ))}
        </List>
      )}
    </Card>
  )
}

export default LoanTransactionList
